import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { DrawerForm } from "../../Common/styles/drawer";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import {
  ModalActionButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import {
  ActiveProfileIcon,
  CalendarIcon,
  RescheduleIcon,
  TimeIcon,
  VideoIcon,
} from "../AppointmentsIcons";
import { NoDataContainer } from "../../Common/styles/table";

type Props = {
  showDrawer: boolean;
  closeDrawer: () => void;
  selectedData?: any;
  refreshPage: () => void;
};

const schema = yup.object().shape({
  date: yup.string().required("Please select a date"),
  appointmentTitle: yup.string().required("Appointment title is Required"),
  doctor: yup.string().required("Doctor is Required"),
  selectedSlotStart: yup.string().required("Appointment time is Required"),
});

const AddAppointmentDrawer: React.FC<Props> = ({
  showDrawer,
  closeDrawer,
  selectedData,
  refreshPage,
}) => {
  const [doctorList, setDoctorList] = useState<any>([]);
  const [scheduling, setScheduling] = useState<string>("started");
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [slotsLoader, setSlotsLoader] = useState<boolean>(false);
  const [availableSlots, setAvailableSlots] = useState<any>([]);

  const studyId = localStorage.getItem("study-id");
  const participantId = localStorage.getItem("user-id");

  const submitHandler = async (values: any) => {
    if (scheduling === "started") {
      setScheduling("inProgress");
    } else if (scheduling === "inProgress") {
      const scheduledFrom = DateTime.fromFormat(
        `${values?.date} ${values?.selectedSlotStart}`,
        "dd/MM/yyyy hh:mm a"
      );
      const scheduledTo = DateTime.fromFormat(
        `${values?.date} ${values?.selectedSlotEnd}`,
        "dd/MM/yyyy hh:mm a"
      );
      try {
        setSubmitLoader(true);
        let url: string = `/participant/study/${studyId}/appointments`;
        let res: AxiosResponse;

        let body: any = {
          participantId: participantId,
          scheduledFrom: scheduledFrom.toUTC().toISO(),
          scheduledTo: scheduledTo.toUTC().toISO(),
          invitorId: values?.doctor,
          mode: "online",
          title: values?.appointmentTitle,
        };

        if (selectedData?.id) {
          await http.post(
            `/participant/study/${studyId}/appointments/${selectedData?.id}/cancel`
          );
        }
        res = await http.post(url, body);

        toastMessage("success", res.data.message);
        setSubmitLoader(false);
        setScheduling("finished");
        refreshPage();
      } catch (err) {
        setSubmitLoader(false);
        errorToastMessage(err as Error);
      }
    } else {
      closeDrawer();
    }
  };

  useEffect(() => {
    async function getDoctor() {
      try {
        setLoading(true);
        let url: string = `/participant/study/${studyId}/appointments/admins`;
        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          name: item?.user?.firstName
            ? item?.user?.lastName
              ? `${item?.user?.firstName} ${item?.user?.lastName}`
              : item?.user?.firstName
            : "",
          adminId: item?.user?.id,
        }));

        setDoctorList(newData);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    }
    getDoctor();
  }, [studyId]);

  const findAvailableSlots = useCallback(
    async (selectedDate: any, selectedDoctor: any) => {
      if (selectedDate && selectedDoctor) {
        try {
          setSlotsLoader(true);
          const startOfDay = selectedDate.startOf("day").toUTC().toISO();
          const endOfDay = selectedDate.endOf("day").toUTC().toISO();
          const url = `/participant/study/${studyId}/appointments/slots?startDate=${startOfDay}&endDate=${endOfDay}&invitorId=${selectedDoctor}`;
          const res = await http.get(url);

          const slots = res?.data?.data?.slots;
          const firstDateKey = Object.keys(slots)[0];
          const newSlotsData = slots[firstDateKey]?.map((item: any) => ({
            startTime: DateTime.fromISO(item?.from).toFormat("hh:mm a"),
            endTime: DateTime.fromISO(item?.to).toFormat("hh:mm a"),
          }));

          setAvailableSlots(newSlotsData);
        } catch (err) {
          errorToastMessage(err as Error);
        } finally {
          setSlotsLoader(false);
        }
      }
    },
    [studyId]
  );

  useEffect(() => {
    if (selectedData?.date && selectedData?.inviterId) {
      const formattedDate = selectedData?.date
        ? DateTime.fromFormat(selectedData?.date, "MMM dd, yyyy")
        : "";

      findAvailableSlots(formattedDate, selectedData.inviterId);
    }
  }, [selectedData?.date, selectedData?.inviterId, findAvailableSlots]);

  return (
    <Modal open={showDrawer} onClose={closeDrawer}>
      <Box
        sx={{
          ...ModalDrawerStyles,
          left: { xs: "0%", sm: "50%" },
          width: { xs: "100%", sm: "50%" },
        }}
      >
        <ModalHeader
          onCloseClick={closeDrawer}
          title={`${
            selectedData?.id ? "Re-schedule" : "Schedule New"
          } Appointment`}
        />

        <Formik
          initialValues={{
            appointmentTitle: selectedData?.title || "",
            date: selectedData?.date
              ? DateTime.fromFormat(
                  selectedData?.date,
                  "MMM dd, yyyy"
                ).toFormat("dd/MM/yyyy")
              : "",
            doctor: selectedData?.inviterId || "",
            displayDoctor: selectedData?.name || "",
            selectedSlotStart: "",
            selectedSlotEnd: "",
          }}
          enableReinitialize
          onSubmit={(values: any) => {
            submitHandler(values);
          }}
          validationSchema={schema}
        >
          {({ getFieldProps, errors, touched, values, setFieldValue }) => (
            <Form style={DrawerForm}>
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={25} />
                </Box>
              ) : scheduling === "started" ? (
                <Box>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>
                      Select Doctor <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Select
                      value={values?.doctor}
                      onChange={(e) => {
                        const selectedDoctor = doctorList?.find(
                          (doc: any) => doc.adminId === e.target.value
                        );
                        setFieldValue("doctor", e.target.value);
                        setFieldValue(
                          "displayDoctor",
                          selectedDoctor?.name || ""
                        );
                        findAvailableSlots(
                          values?.date
                            ? DateTime.fromFormat(values?.date, "dd/MM/yyyy")
                            : "",
                          e.target.value
                        );
                      }}
                    >
                      {doctorList?.length > 0 ? (
                        doctorList?.map((item: any) => (
                          <MenuItem key={item.adminId} value={item.adminId}>
                            {item.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem key={"None"} value={""} disabled>
                          No Data available
                        </MenuItem>
                      )}
                    </Select>
                    <FormHelperText
                      error={touched?.doctor && errors?.doctor ? true : false}
                    >
                      {touched?.doctor && errors?.doctor
                        ? (errors?.doctor as string)
                        : " "}
                    </FormHelperText>
                  </FormControl>

                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="appointment-title">
                      Appointment Title <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <TextField
                      fullWidth
                      id="appointment-title"
                      {...getFieldProps("appointmentTitle")}
                      error={
                        touched?.appointmentTitle && errors?.appointmentTitle
                          ? true
                          : false
                      }
                      helperText={
                        touched?.appointmentTitle && errors?.appointmentTitle
                          ? (errors?.appointmentTitle as string)
                          : " "
                      }
                      placeholder="Appointment Title"
                    />
                  </FormControl>

                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="date">
                      Appointment Date <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <DatePicker
                      disablePast
                      format="dd/MM/yyyy"
                      value={
                        values?.date
                          ? DateTime.fromFormat(values?.date, "dd/MM/yyyy")
                          : null
                      }
                      onChange={(newDateValue: any) => {
                        setFieldValue(
                          "date",
                          newDateValue.toFormat("dd/MM/yyyy")
                        );
                        findAvailableSlots(newDateValue, values?.doctor);
                      }}
                      slotProps={{
                        textField: {
                          inputProps: {
                            readOnly: true,
                            placeholder: "Select date",
                            id: "date",
                          },
                          error: touched?.date && errors?.date ? true : false,
                          helperText:
                            touched?.date && errors?.date
                              ? (errors?.date as string)
                              : " ",
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>
                      Appointment Time <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    {slotsLoader ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 4,
                        }}
                      >
                        <CircularProgress size={25} />
                      </Box>
                    ) : (
                      availableSlots?.length > 0 && (
                        <Box
                          display="grid"
                          gridTemplateColumns="repeat(auto-fill, minmax(160px, 1fr))"
                          gap={1}
                        >
                          {availableSlots?.map((item: any, index: number) => {
                            const start = DateTime.fromFormat(
                              item?.startTime,
                              "hh:mm a"
                            ).toFormat("HH:mm");
                            const end = DateTime.fromFormat(
                              item?.endTime,
                              "hh:mm a"
                            ).toFormat("HH:mm");
                            const timeRange = `${start}-${end}`;
                            const isSelected =
                              values.selectedSlotStart === item.startTime &&
                              values.selectedSlotEnd === item.endTime;

                            return (
                              <Box
                                key={index}
                                onClick={() => {
                                  setFieldValue(
                                    "selectedSlotStart",
                                    item.startTime
                                  );
                                  setFieldValue(
                                    "selectedSlotEnd",
                                    item.endTime
                                  );
                                }}
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  height: "60px",
                                  cursor: "pointer",
                                  borderRadius: "12px",
                                  bgcolor: isSelected ? "primary.main" : "#fff",
                                  border: "1px solid",
                                  borderColor: "primary.main",
                                }}
                              >
                                <Typography
                                  fontWeight={isSelected ? 500 : 400}
                                  color={isSelected ? "#fff" : "primary.main"}
                                >
                                  {timeRange}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )
                    )}
                    {!slotsLoader && !availableSlots?.length && (
                      <NoDataContainer>
                        <Typography variant="body1" color="gray">
                          No Slots Available
                        </Typography>
                      </NoDataContainer>
                    )}
                    <FormHelperText
                      error={
                        touched?.selectedSlotStart && errors?.selectedSlotStart
                          ? true
                          : false
                      }
                    >
                      {touched?.selectedSlotStart && errors?.selectedSlotStart
                        ? (errors?.selectedSlotStart as string)
                        : " "}
                    </FormHelperText>
                  </FormControl>
                </Box>
              ) : scheduling === "inProgress" ? (
                <Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontweight: 500,
                        color: "text.primary",
                      }}
                    >
                      You're almost there
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Please confirm the time and date or change the details.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: 1,
                      backgroundColor: "#fff",
                      my: 2,
                      boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <ActiveProfileIcon />
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "18px",
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {values?.displayDoctor}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "text.secondary",
                            }}
                          >
                            {values?.appointmentTitle}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            aliignItems: "center",
                            mt: 1,
                            gap: 3,
                            flexDirection: { xs: "column", md: "row" },
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarIcon />
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              {values?.date}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TimeIcon />
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              {values?.selectedSlotStart} -{" "}
                              {values?.selectedSlotEnd}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "#f2f2f2",
                        },
                      }}
                      onClick={() => setScheduling("started")}
                    >
                      <Typography
                        fontSize={"18px"}
                        fontWeight={500}
                        color="text.primary"
                      >
                        Change appointment
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      height: "104px",
                      width: "104px",
                      "& .image": {
                        maxWidth: "100%",
                        maxHeight: "100%",
                      },
                    }}
                  >
                    <img
                      src={"/AppointmentConfirm.gif"}
                      alt={"Appointment Confirmation"}
                      className="image"
                      style={{ objectFit: "contain" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "30px",
                      fontWeight: 600,
                      color: "text.primary",
                      my: 3,
                    }}
                  >
                    Appointment confirmed!
                  </Typography>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "21px",
                        fontWeight: 500,
                        color: "text.secondary",
                        mb: 2,
                      }}
                    >
                      About your consultation
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <VideoIcon />
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        Joining link will be shown on the home screen 15min
                        before the consultation
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <RescheduleIcon />
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        You can reschedule this session up to 4 hours before the
                        consultation
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={{ ...ModalActionButtonStyles, mt: 2, mb: 2 }}>
                  {!submitLoader ? (
                    scheduling === "started" || scheduling === "inProgress" ? (
                      <>
                        <Button onClick={closeDrawer} variant="outlined">
                          Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                          {(scheduling === "started" && "Next") ||
                            (scheduling === "inProgress" &&
                              "Proceed to confirm")}
                        </Button>
                      </>
                    ) : (
                      <Button type="submit" variant="contained">
                        Done
                      </Button>
                    )
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddAppointmentDrawer;
